import React from 'react';
import { Accordion, Button, Card, Badge } from 'react-bootstrap';
import { FaTruck, FaUser, FaBarcode, FaWeightHanging, FaBoxOpen, FaClock } from 'react-icons/fa';

interface Props {
    shipments: any[];
    onInspect: (shipmentId: number) => void;
    onEdit: (shipment: any) => void;
    onComplete: (shipmentId: number) => void;
    onUnconfirm: (shipmentId: number) => void;
}

const ShipmentList: React.FC<Props> = ({ shipments, onInspect, onEdit, onComplete, onUnconfirm }) => {

    const formatDate = (dateString?: string) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleString('tr-TR', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
        });
    };

    if (shipments.length === 0) {
        return (
            <div className="text-center p-5 text-muted border rounded bg-light mt-4">
                <h4>📭 Yüklenecek Sevkiyat Yok</h4>
                <p className="mb-0">Şu anda işlem bekleyen aktif bir sevkiyat bulunmuyor.</p>
            </div>
        );
    }

    return (
        <Accordion defaultActiveKey="0" className="mt-4">
            {shipments.map((shipment, index) => (
                <Accordion.Item
                    eventKey={shipment.id.toString()}
                    key={shipment.id}
                    // 🔥 DEĞİŞİKLİK 1: 'border-0' kaldırıldı, 'border' eklendi.
                    // 'border-secondary' eklenerek gri tonu belirginleştirildi ama çok koyu değil (subtle).
                    className="mb-3 shadow-sm border border-secondary-subtle overflow-hidden rounded"
                    style={{ borderLeft: "6px solid #0d6efd" }}
                >
                    <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100 pe-3 flex-wrap gap-3">

                            {/* 1. GRUP: Araç ve Kod */}
                            <div className="d-flex align-items-center gap-3">
                                <div className="d-flex align-items-center gap-2 text-primary">
                                    <FaTruck size={20} />
                                    <span className="fw-bold fs-5 text-dark">
                                        {shipment.vehicleDescription || "PLAKA YOK"}
                                    </span>
                                </div>
                                <div className="vr d-none d-md-block"></div>
                                <div className="d-flex align-items-center gap-2 text-muted">
                                    <FaBarcode />
                                    <small>{shipment.shipmentCode}</small>
                                </div>
                            </div>

                            {/* 2. GRUP: Sürücü (Varsa) */}
                            {shipment.driverFullName && (
                                <div className="d-flex align-items-center gap-2 text-secondary d-none d-md-flex">
                                    <FaUser />
                                    <span className="small">{shipment.driverFullName}</span>
                                </div>
                            )}

                            {/* 3. GRUP: Butonlar */}
                            <div className="d-flex gap-2 ms-auto align-items-center">
                                <Button
                                    size="sm"
                                    variant="warning"
                                    className="fw-bold text-dark"
                                    onClick={(e) => { e.stopPropagation(); onEdit(shipment); }}
                                >
                                    📥 Yükle
                                </Button>

                                <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); onInspect(shipment.id); }}>
                                    İncele
                                </Button>

                                <Button size="sm" variant="success" onClick={(e) => { e.stopPropagation(); onComplete(shipment.id); }}>
                                    Tamamla
                                </Button>

                                <div className="vr mx-1"></div>
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={(e) => { e.stopPropagation(); onUnconfirm(shipment.id); }}
                                    title="Planlamaya Geri Gönder"
                                >
                                    Geri Al
                                </Button>
                            </div>
                        </div>
                    </Accordion.Header>

                    <Accordion.Body className="bg-light">
                        <div className="row g-3">
                            <div className="col-12">
                                {/* 🔥 DEĞİŞİKLİK 2: İç kartın da 'border-0'ı kaldırıldı, ince bir çerçeve eklendi */}
                                <Card className="border shadow-sm p-3">
                                    <div className="row text-center">

                                        {/* Durum */}
                                        <div className="col-md-3 mb-2 mb-md-0 border-end">
                                            <small className="text-muted d-block">Durum</small>
                                            <Badge bg="info" className="fs-6 fw-normal">
                                                {shipment.statusDetail?.label || "LOADING"}
                                            </Badge>
                                        </div>

                                        {/* Toplam Ürün */}
                                        <div className="col-md-3 mb-2 mb-md-0 border-end">
                                            <small className="text-muted d-block">Toplam Kalem</small>
                                            <div className="fw-bold fs-5 text-dark">
                                                <FaBoxOpen className="text-secondary me-2" />
                                                {shipment.totalItemCount ?? "0"}
                                            </div>
                                        </div>

                                        {/* Yüklenen Ağırlık */}
                                        <div className="col-md-3 mb-2 mb-md-0 border-end">
                                            <small className="text-muted d-block">Yüklenen Ağırlık</small>
                                            <div className="fw-bold fs-5 text-success">
                                                <FaWeightHanging className="me-2" />
                                                {shipment.loadedWeightInTons ?? "-"} Ton
                                            </div>
                                        </div>

                                        {/* Son Güncelleme */}
                                        <div className="col-md-3">
                                            <small className="text-muted d-block">Son İşlem</small>
                                            <div className="text-secondary">
                                                <FaClock className="me-1" />
                                                {formatDate(shipment.modifiedAt)}
                                            </div>
                                        </div>

                                    </div>
                                </Card>
                            </div>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            ))}
        </Accordion>
    );
};

export default ShipmentList;